import { inject, Injectable, OnDestroy, signal } from '@angular/core';

import {
  ActorToApplications,
  ALREADY_OPEN_MESSAGE,
  CatalogComponent,
  DeployedActorToApplication,
  EntityInput,
  ERROR_MESSAGE,
  NEW_TAB_MESSAGE,
  ParameterMergedResult,
  ParameterMergedSource,
  ParameterMergedValueSources,
  SEARCH_REGEX,
  SingleTroubleshootingResult,
  TroubleshootingAppInfo,
  TroubleshootingComponentFamily,
  TroubleshootingResult,
  TroubleshootingResultKey,
  TroubleshootingSearch,
  TroubleshootingSearchInputs,
  TroubleshootingSearchWithType,
  TroubleshootingTableRowData
} from './troubleshooting.datamodel';
import { ASReefRESTService } from '../../core/rest.service';
import { forkJoin, map, of } from 'rxjs';
import { ActorObject, ActorType } from '../../shared/datamodel';
import { ToastNotificationsService } from '../../core/toast-notifications/toast-notifications.service';
import {
  searchInputs,
  troubleshootingFamilyToComponentTypes,
  troubleshootingSearchTypeToActorType
} from './troubleshooting.strategies';
import { ComponentObject, ComponentType, ComponentWithConfigurationsObject } from '../../publish/component/store/component.datamodel';
import { ToastNotifications } from '../../core/toast-notifications/toast-notifications.datamodel';
import { TroubleshootingCacheService } from './troubleshooting-cache.service';
import {
  BAD_FORMAT,
  commentsMap,
  StatusOptions
} from '../feature/troubleshooting-results/troubleshooting-result/troubleshooting-result.datamodel';
import { AppObject } from '../../application/app.datamodel';
import { ParametersOverrideService } from './parameters-override.service';

/**
 * Support service
 */
@Injectable()
export class TroubleshootingService implements OnDestroy {
  readonly results = signal<TroubleshootingResult[]>([]);
  readonly selectedResult = signal<TroubleshootingResultKey | undefined>(undefined);
  private readonly toastNotificationsService = inject(ToastNotificationsService);
  private readonly troubleshootingCache = inject(TroubleshootingCacheService);
  private readonly reefRESTService = inject(ASReefRESTService);
  private readonly parametersOverride = inject(ParametersOverrideService);

  constructor() {
    this.results.set(this.troubleshootingCache.results());
    this.selectedResult.set(this.troubleshootingCache.selectedResult());
  }

  ngOnDestroy(): void {
    this.troubleshootingCache.cacheTroubleshooting(
      this.results(),
      this.selectedResult()
    );
  }

  /**
   * Search for the troubleshooting information by actor.
   * Update the result signal with the information.
   * @param searchItem Object with the troubleshooting search information
   */
  search(searchItem: TroubleshootingSearchWithType): void {
    const sanitizedSearchItem = this.sanitizeTroubleshootingSearch(searchItem);
    const key: TroubleshootingResultKey = this.getTroubleshootingResultKey(searchItem);
    if (!this.isKeyAlreadyInResult(key)) {
      this.reefRESTService.get(
          this.buildTroubleshootingSearchURL(sanitizedSearchItem)
      ).subscribe({
        next: output => {
          if (Object.keys(output).length === 0) {
            this.handleEmptyCatalog(sanitizedSearchItem);
          } else {
            this.handleTroubleshootingSearchCatalog(output, sanitizedSearchItem, key);
          }
        },
        error: (httpError: {error: {message: string}[]}) => {
          const toastNotifications: ToastNotifications[] = httpError.error?.filter(error => !!error.message)
            .map(error => { return {message: error.message, type: 'danger'};});
          if (toastNotifications?.length > 0) {
            toastNotifications.forEach(notification => this.toastNotificationsService.showToast(notification));
          } else {
            this.toastNotificationsService.showToast({
              message: ERROR_MESSAGE,
              type: 'danger'
            });
          }
        }
      });
    } else {
      this.toastNotificationsService.showToast({
        message: ALREADY_OPEN_MESSAGE,
        type: "info"
      });
      this.selectedResult.set(key);
    }
  }

  /**
   * Remove the specified actor from the search result
   * @param key
   */
  removeKeyFromSearch(key: TroubleshootingResultKey): void {
    const actual = this.results();
    const updated = this.getEntriesWithoutKey(actual, key);
    this.results.set(updated);
    this.selectedResult.set(updated[0]?.key);
  }

  /**
   * Get the entry with the specified key
   * @param entries
   * @param key
   */
  getEntryWithKey(entries: TroubleshootingResult[], key: TroubleshootingResultKey):
      TroubleshootingResult | undefined {
    return entries.find(entry => entry.key.actor.actorName === key.actor.actorName &&
        entry.key.actor.actorType === key.actor.actorType && entry.key.officeId === key.officeId);
  }

  /**
   * Extract the Table rows data from the troubleshooting search's result
   * @param result
   */
  extractTableRowsFromTroubleShootingResult(result: SingleTroubleshootingResult, orderedActors: ActorObject[]):
      TroubleshootingTableRowData[] {
    const rows: TroubleshootingTableRowData[] = [];

    const parameterComponents: {component: ComponentWithConfigurationsObject, access: DeployedActorToApplication}[] = [];
    const appEntries = Object.entries(result).map(([appId, appInfo]: [string, TroubleshootingAppInfo]) => {
      const access = this.buildAccessForApplication(appInfo.access, appId);
      if (access) {
        const components = appInfo.application.components?.map(c => {
          const configuration = appInfo.configurations?.[c.id]?.find(conf =>
            conf.guid === access.application.configurations?.[c.id]) ?? (c as any)?.configuration;
          const defaultConfiguration = appInfo.configurations?.[c.id]?.find(conf =>
            conf.guid === c.id || !conf.parentGuid);
          const component: ComponentWithConfigurationsObject = {
            ...c,
            configuration,
            defaultConfiguration
          };
          if (!access.application.isExcluded && troubleshootingFamilyToComponentTypes(TroubleshootingComponentFamily.PARAMETERS).includes(c.componentType)) {
            parameterComponents.push({component, access});
          }
          return component;
        });

        return {
          application: {
            ...appInfo.application,
            components
          },
          access
        };
      }
      return undefined;
    }).filter(entry => entry !== undefined);
    const actorsInPriorityOrder = this.sortByPriority(orderedActors, (actor: ActorObject) => actor);
    const parameterComponentsOverride = this.mergeParameterOverrides(parameterComponents, actorsInPriorityOrder);

    appEntries.forEach(entry => {
      entry.application.components?.forEach(component => {
        const newRows = this.componentAndConfigToRows(entry?.application, component, entry?.access, parameterComponentsOverride, actorsInPriorityOrder);
        rows.push(...newRows);
      });
    });
    return rows;
  }


  /**
   * Merge the parameters from the components and configurations
   * @param parameterComponents array of components with their deployed configuration
   * @param access the access to the deployed actor and application
   * @param actorsInPriorityOrder array of ordered actors to merge the parameters for (e.g. USER, OFFICE, OGU_ORGANISATION, ORGANISATION)
   * @private
   * @returns Map<string, ParameterMergedResult> map of actor name to ParameterMergedResult
   */
  private mergeParameterOverrides(
    parameterComponents: { component: ComponentWithConfigurationsObject, access: DeployedActorToApplication }[],
    actorsInPriorityOrder: ActorObject[]
  ): Map<string, ParameterMergedResult> {
    const actorToParametersMapping = new Map<string, ParameterMergedResult>();

    const mergingStructure = this.toMergeStructure(parameterComponents);

    for (const actor of actorsInPriorityOrder) {
      const actorKey = actor.actorName;
      const actorMergeMap = mergingStructure.get(actorKey);
      if (actorMergeMap) {
        const merged = this.parametersOverride.mergeOverrideParameters([actorMergeMap]);
        if (merged && (merged.overrides.size > 0 || merged.conflicts.size > 0)) {
          actorToParametersMapping.set(actorKey, { conflicts: merged.conflicts, overrides: this.parseParametersFromMap(merged.overrides)});
        }
      }
    }
    return actorToParametersMapping;
  }

  /**
   * Convert the parameter components to a merge structure
   * Map of actor name to parameter name to value to array of ParameterMergedData
   *
   * @param parameterComponents
   * @returns Map<string, Map<string, Map<string, Array<ParameterMergedData>>>>
   *  map of actor name to parameter name to value to array of ParameterMergedData (appId, componentId)
   * @private
   */
  private toMergeStructure(
    parameterComponents: {component: ComponentWithConfigurationsObject, access: DeployedActorToApplication}[]
  ): Map<string, Map<string, Map<string, Array<ParameterMergedSource>>>> {
    const output = new Map<string, Map<string, Map<string, Array<ParameterMergedSource>>>>();

    parameterComponents.forEach(({ component, access }) => {
      let parsedContent: Record<string, string> = {};
      if (component.componentType === ComponentType.PERS_SETTINGS) {
        parsedContent = this.parseContent(component.content);
      } else if (component.componentType === ComponentType.FUNCTIONAL_PARAM || component.componentType === ComponentType.ACTIVATION_PARAM) {
        parsedContent = this.getFunctionalParamOverrides(component);
      }

      const actorKey = access.actor.actorName;
      if (!output.has(actorKey)) {
        output.set(actorKey, new Map());
      }
      const actorOverrides = output.get(actorKey);

      Object.entries(parsedContent).forEach(([paramName, paramValue]) => {
        if (!actorOverrides?.has(paramName)) {
          actorOverrides?.set(paramName, new Map());
        }
        const valueMap = actorOverrides?.get(paramName);
        if (!valueMap?.has(paramValue)) {
          valueMap?.set(paramValue, []);
        }
        valueMap?.get(paramValue)?.push(this.generateParameterMergedData(component, access));
      });
    });

    return output;
  }

  /**
   * Parse the content of the component
   * @param content
   * @private
   */
  private parseContent(content: any): Record<string, string> {
    try {
      return typeof content === 'string' ? JSON.parse(content) : {};
    } catch {
      return {};
    }
  }

  /**
   * Get the functional parameter overrides
   * @param component
   * @param configurations
   * @private
   */
  private getFunctionalParamOverrides(component: ComponentWithConfigurationsObject): Record<string, string> {
    return {
      ...this.parseContent(component.defaultConfiguration?.content),
      ...this.parseContent(component.configuration?.content)
    };
  }

  /**
   * Parse the parameters from the map
   * @param inputMap
   * @private
   */
  private parseParametersFromMap(inputMap: Map<string, ParameterMergedValueSources>): Map<string, ParameterMergedValueSources> {
    const SITE_PREFIX = 'SITE_';
    const output: Map<string, ParameterMergedValueSources> = new Map<string, ParameterMergedValueSources>();
    for (const [key, value] of inputMap.entries()) {
      if (!key.startsWith(SITE_PREFIX) || !(key.slice(SITE_PREFIX.length) in inputMap)) {
        output.set(key, value);
      }
    }
    return output;
  }

  /**
   * Generate log data for the component
   * @param component
   * @param access
   * @private
   */
  private generateParameterMergedData(component: any, access: any): ParameterMergedSource {
    return {
      appId: access.application.guid,
      componentId: component.id,
    };
  }

  /**
   * Handle the troubleshooting search catalog
   * @param output the output of the troubleshooting search
   * @param sanitizedSearchItem the item searched for
   * @param key the key being searched
   * @private
   */
  private handleTroubleshootingSearchCatalog(output: ActorToApplications[],
                                             sanitizedSearchItem: TroubleshootingSearchWithType,
                                             key: TroubleshootingResultKey): void {
    const idsToHasConfiguration = this.getAppIdsFromActorsAndAccessMap(output);
    const isProductSearch = sanitizedSearchItem.entityType === 'product';
    const org = output.find(actorToApplication => actorToApplication.actor.actorType === ActorType.ORGANISATION)?.actor;
    sanitizedSearchItem.org = org?.actorName || sanitizedSearchItem.org;
    const ownedByQueryParam = `?ownedBy=ORGANISATION.${sanitizedSearchItem.org?.toUpperCase()}`;
    const appIds = Object.keys(idsToHasConfiguration);
    const requests = appIds.map((appId: string) =>
      forkJoin([
        this.reefRESTService.get(`applications/${appId}`),
        !isProductSearch && idsToHasConfiguration[appId]? this.reefRESTService.get(`applications/${appId}/components/configurations${ownedByQueryParam}`): of([]),
      ]).pipe(
        map(([application, configurations]) => ({
          appId,
          application,
          configurations: configurations?.cmpConfigurationsMap
        }))
      )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        results.forEach(({ appId, application, configurations }) => {
          this.updateTroubleshootingResult(
            key,
            appId,
            output,
            application,
            configurations,
            sanitizedSearchItem
          );
        });
        this.toastNotificationsService.showToast({
          message: NEW_TAB_MESSAGE,
          type: 'success'
        });
        this.selectedResult.set(key);
      },
      error: ({ error }) => this.showError(error?.message)
    });
  }

  /**
   * Check if the provided key is already in the result
   * @param key
   * @private
   */
  private isKeyAlreadyInResult(key: TroubleshootingResultKey) {
    return this.results().some(
      entry => entry.key.actor.actorName.toUpperCase() === key.actor.actorName.toUpperCase() &&
        entry.key.actor.actorType === key.actor.actorType &&
        entry.key.officeId?.toUpperCase() === key.officeId?.toUpperCase()
      );
  }

  /**
   * Notify the user with an error message
   *
   * @param message Error message to be displayed
   * @private
   */
  private showError(message: string = ERROR_MESSAGE): void {
    this.toastNotificationsService.showToast({
      message,
      type: 'danger'
    });
  }

  /**
   * Sanitize the troubleshooting search input
   * @param searchItem
   * @private
   */
  private sanitizeTroubleshootingSearch(searchItem: TroubleshootingSearchWithType): TroubleshootingSearchWithType {
    return {
      entityType: searchItem.entityType,
      ...Object.entries(searchItem).reduce((acc: TroubleshootingSearch, [key, value]) => {
        const sanitizedValue: string = value.trim();
        if (!SEARCH_REGEX.test(sanitizedValue)) {
          throw new Error(`Invalid input for ${key}: ${value}`);
        }
        acc[key as keyof TroubleshootingSearch] = key === 'product' ? sanitizedValue :
            encodeURIComponent(sanitizedValue);
        return acc;
      }, {})
    };
  }

  /**
   * Build the troubleshooting search URL
   * @param searchItem
   * @private
   */
  private buildTroubleshootingSearchURL(searchItem: TroubleshootingSearchWithType): string {
    const requestTypes: EntityInput[] = searchInputs(searchItem.entityType);
    if (searchItem.entityType !== 'product') {
      requestTypes.push(TroubleshootingSearchInputs.product);
    }
    let uri = "troubleshooting/applications?";
    requestTypes.forEach(entityType => {
      if (searchItem[entityType.type]) {
        const actorType = troubleshootingSearchTypeToActorType(entityType.type);
        let actorName = entityType.type === 'product' ? 'SELL_CONNECT' :
            searchItem[entityType.type];
        if (actorType === ActorType.OGU_ORGANISATION || actorType === ActorType.USER_ORGANISATION) {
          actorName += `_${searchItem.org}`;
        }
        uri += `${entityType.type}=${actorType}.${actorName?.toUpperCase()}&`;
      }
    });
    return uri.substring(0, uri.length - 1);
  }

  /**
   * Build a troubleshootingResultKey to represent the search item
   * @param searchItem
   * @private
   */
  private getTroubleshootingResultKey(searchItem: TroubleshootingSearchWithType): TroubleshootingResultKey {
    const resultKey: TroubleshootingResultKey = {
      actor: this.getActorObjectFromSearchItem(searchItem)
    };
    const searchEntities: EntityInput[] = searchInputs(searchItem.entityType);
    if (searchEntities.some(entity => entity.type === 'office')) {
      resultKey.officeId = searchItem.office?.toUpperCase();
    }
    return resultKey;
  }

  /**
   * Get the actor object from the search item
   * @param searchItem
   * @private
   */
  private getActorObjectFromSearchItem(searchItem: TroubleshootingSearchWithType): ActorObject {
    let actorName = searchItem[searchItem.entityType] as string;
    const actorType = troubleshootingSearchTypeToActorType(searchItem.entityType);
    if ([ActorType.USER_ORGANISATION, ActorType.OGU_ORGANISATION].includes(actorType)) {
      actorName += `_${searchItem.org}`;
    }
    return {
      actorName: actorName.toUpperCase(),
      actorType: actorType
    };
  }

  /**
   * Update the troubleshooting result with the new actor's information
   *
   * @param key result key
   * @param appId Application id
   * @param actorToApplications Map of actors and applications
   * @param application Application information
   * @param configurations Application configurations
   * @param searchItem Search item
   * @private
   */
  private updateTroubleshootingResult(key: TroubleshootingResultKey, appId: string,
                                      actorToApplications: ActorToApplications[],
                                      application: any, configurations: any,
                                      searchItem: TroubleshootingSearchWithType): void {
    this.results.update((previous: TroubleshootingResult[]): TroubleshootingResult[] => {
      const actorEntry = this.getEntryWithKey(previous, key);
      const newActorEntry: TroubleshootingResult = {
        key: key,
        searchItem: searchItem,
        orderedActors: actorToApplications.map(actorToApp => actorToApp.actor),
        result: {
          ...actorEntry?.result,
          [appId]: {
            access: actorToApplications.filter(actorToApp =>
                actorToApp.applications?.map(app => app.guid).includes(appId)),
            configurations: configurations,
            application: application
          }
        }
      };
      if (!actorEntry) {
        return [...previous, newActorEntry];
      }
      const previousExceptNewActor = this.getEntriesWithoutKey(previous, key);
      return [...previousExceptNewActor, newActorEntry];
    });
  }

  /**
   * Update the troubleshooting result with an empty entry
   * @param searchItem
   * @private
   */
  private handleEmptyCatalog(searchItem: TroubleshootingSearchWithType): void {
    const actor = this.getActorObjectFromSearchItem(searchItem);
    const key = {actor: actor, officeId: searchItem.office};
    this.results.update((previous: TroubleshootingResult[]): TroubleshootingResult[] => {
      const actorEntry = this.getEntryWithKey(previous, key);
      const newActorEntry: TroubleshootingResult = {key: key, searchItem: searchItem, result: {}, orderedActors: []};
      if (!actorEntry) {
        return [...previous, newActorEntry];
      }
      return [...previous];
    });
    this.selectedResult.set(key);
    this.toastNotificationsService.showToast({
      message: NEW_TAB_MESSAGE,
      type: 'success'
    });
  }

  /**
   * Get the app ids from the actors and access map
   * @param actorToApplications
   * @private
   */
  private getAppIdsFromActorsAndAccessMap(actorToApplications: ActorToApplications[]): {[applicationId: string]: boolean} {
    const result: {[applicationId: string]: boolean} = {};
    actorToApplications.forEach(actorToApplication => {
      actorToApplication.applications?.forEach(application => {
        const hasConfigurations: boolean = application.configurations ? Object.keys(application.configurations).length > 0 : false;
        result[application.guid] = result[application.guid] || hasConfigurations;
      });
    });
    return result;
  }

  /**
   * Get the entries without the specified key
   * @param entries
   * @param key
   * @private
   */
  private getEntriesWithoutKey(entries: TroubleshootingResult[], key: TroubleshootingResultKey):
      TroubleshootingResult[] {
    return entries.filter(entry => entry.key.actor.actorName !== key.actor.actorName ||
        entry.key.actor.actorType !== key.actor.actorType || entry.key.officeId !== key.officeId);
  }

  /**
   * Constructs access information for a specific application by analyzing the provided list of accesses.
   * Config overrides are collected separately and applied only to already-deployed (non-excluded) components 
   * after the main deployment/exclusion pass.
   *
   * Processing order (least to most specific): ORGANISATION > OGU > OFFICE > USER > PRODUCT
   *
   * @param accesses A prioritized list of actor-to-application mappings to evaluate.
   * @param appId The unique identifier of the application for which access information is being built.
   * @returns The deployed access details or `null` if no active access is found.
   * @private
   */
  private buildAccessForApplication(accesses: ActorToApplications[], appId: string): DeployedActorToApplication | null {
    const orderedAccesses = this.sortByPriority(accesses, (access: ActorToApplications) => access.actor);
    let deployedAccess: DeployedActorToApplication | null = null;
    let configOverride: { actor: ActorObject, application: CatalogComponent } | null = null;

    for (const access of orderedAccesses) {
      const application = access.applications.find((app: CatalogComponent) => app.guid === appId) as CatalogComponent;
      if (!application) {
        continue;
      }
      if (application.isConfigurationOverride) {
        configOverride = { actor: access.actor, application };
      } else if (application.isExcluded) {
        this.applyExclusion(access.actor, deployedAccess);
        deployedAccess ??= this.initDeployedAccess(access.actor, application);
      } else {
        deployedAccess = this.applyDeployment(access.actor, application, deployedAccess);
      }
    }

    if (configOverride && deployedAccess && !deployedAccess.application.isExcluded) {
      this.applyConfigOverride(configOverride.actor, configOverride.application, deployedAccess);
    }

    return deployedAccess;
  }

  /**
   * Creates the initial deployed access state for an application entry.
   * @param actor The actor that owns this entry.
   * @param application The catalog component data.
   * @returns A new DeployedActorToApplication with excludedBy set if the entry is excluded.
   */
  private initDeployedAccess(actor: ActorObject, application: CatalogComponent): DeployedActorToApplication {
    return {
      actor,
      application: { ...application, excludedBy: application.isExcluded ? actor : undefined }
    };
  }

  /**
   * Marks an existing deployed access as excluded. No-op if there is no deployment yet or it is already excluded.
   * @param actor The actor applying the exclusion.
   * @param deployedAccess The current deployment state (may be null).
   */
  private applyExclusion(actor: ActorObject, deployedAccess: DeployedActorToApplication | null): void {
    if (!deployedAccess || deployedAccess.application.isExcluded) {
      return;
    }
    deployedAccess.application.isExcluded = true;
    deployedAccess.application.excludedBy = actor;
  }

  /**
   * Applies a regular deployment. If no deployment exists yet, initializes one.
   * If the component was previously excluded, a more specific deployment un-excludes it.
   * @param actor The actor performing the deployment.
   * @param application The catalog component data for this deployment.
   * @param deployedAccess The current deployment state (may be null).
   * @returns The updated (or newly created) deployed access.
   */
  private applyDeployment(
    actor: ActorObject,
    application: CatalogComponent,
    deployedAccess: DeployedActorToApplication | null
  ): DeployedActorToApplication {
    if (!deployedAccess) {
      return this.initDeployedAccess(actor, application);
    }
    if (deployedAccess.application.isExcluded) {
      deployedAccess.application.isExcluded = false;
      deployedAccess.application.excludedBy = undefined;
    }
    deployedAccess.actor = actor;
    deployedAccess.application = { ...application, excludedBy: undefined };
    return deployedAccess;
  }

  /**
   * Applies a configuration override to an existing deployment. Updates the configuration
   * fields and marks the deployment actor as the override actor (for display purposes).
   * @param actor The actor that owns the config override.
   * @param application The catalog component containing the override configuration.
   * @param deployedAccess The existing deployed access to apply the override to.
   */
  private applyConfigOverride(
    actor: ActorObject,
    application: CatalogComponent,
    deployedAccess: DeployedActorToApplication
  ): void {
    const mergedConfigurations = {
      ...deployedAccess.application.configurations,
      ...application.configurations
    };
    deployedAccess.actor = actor;
    deployedAccess.application = { ...application, configurations: mergedConfigurations, excludedBy: undefined };
  }

  /**
   * Sort objects related to actors by the priority of their actors, keeping product objects at the end
   * @param objects the objects to sort, ordered by increasing deployment size:
   *      USER > OFFICE > OGU_ORGANISATION > ORGANISATION > PRODUCT
   * @param actorAccessor a function to access the actor from an object
   * @returns the sorted objects ordered by priority:
   * ORGANISATION > OGU_ORGANISATION > OFFICE > USER > PRODUCT
   */
  private sortByPriority(objects: any[], actorAccessor: (object: any) => ActorObject): any[] {
    let orderedObjects: any[];
    const productAccess = objects.find(object => actorAccessor(object).actorType === ActorType.PRODUCT);
    if (productAccess) {
      const otherAccesses = objects.filter(object => actorAccessor(object).actorType !== ActorType.PRODUCT);
      orderedObjects = [...[...otherAccesses].reverse(), productAccess];
    } else {
      orderedObjects = [...objects].reverse();
    }
    return orderedObjects;

  }

  /**
   * Convert the component and its configuration to rows in the table
   * @param appInfo
   * @param component
   * @param access
   * @param componentConfiguration
   * @param parameters
   * @private
   */
  private componentAndConfigToRows(
      appInfo: {app: Partial<AppObject>, components: ComponentObject[]},
      component: ComponentWithConfigurationsObject,
      access: DeployedActorToApplication,
      parameters: Map<string, ParameterMergedResult>,
      actorsInPriorityOrder: ActorObject[]): TroubleshootingTableRowData[] {
    let status = StatusOptions.active;
    if (access.application.isExcluded) {
      status = StatusOptions.excluded;
    } else if (access.application.isConfigurationOverride) {
      status = StatusOptions.configurationOverride;
    }

    const commentActor = status === StatusOptions.excluded
      ? access.application.excludedBy?.actorName ?? ''
      : access.actor.actorName;
    const comment = commentsMap[status](commentActor);

    const commonRowData: TroubleshootingTableRowData = {
      appType: appInfo.app.applicationType as any,
      appId: appInfo.app.id as string,
      appName: appInfo.app.name as string,
      componentType: component.componentType,
      componentName: component.name,
      componentId: component.id,
      actorType: access.actor.actorType as any,
      actorName: access.actor.actorName,
      status,
      comment,
      deploymentTimestamp: new Date(access.application.updatedOn).toUTCString(),
      configurationId: component.configuration?.guid ?? '-',
      configurationName: component.configuration?.name ?? '-',
      configurationContent: this.formatConfigurationContent(component),
    };

     if (troubleshootingFamilyToComponentTypes(TroubleshootingComponentFamily.PARAMETERS)
        .includes(component.componentType)) {
      return this.handleParameterRow(component, commonRowData, parameters, access.actor, actorsInPriorityOrder);
    } else if (troubleshootingFamilyToComponentTypes(TroubleshootingComponentFamily.SCRIPT)
        .includes(component.componentType)) {
      return this.handleScriptRow(component, commonRowData);
    } else {
      return [];
    }
  }

  /**
   * Format JSON content for table display
   * @param content content to format
   * @returns formatted content or '-' if undefined
   * @private
   */
  private formatConfigurationContent(component: ComponentWithConfigurationsObject): string {
    if (!component.configuration) {
      return '-';
    }
    return JSON.stringify({
      ...this.parseContent(component.defaultConfiguration?.content),
      ...this.parseContent(component.configuration?.content)
    }, null, 2);
  }

  /**
   * Handle the parameters row in the table
   * @param component component object
   * @param componentConfiguration configuration object
   * @param commonRowData row data common in component families
   * @param parameters
   * @param actor
   * @private
   */
  private handleParameterRow(component: ComponentWithConfigurationsObject, commonRowData: TroubleshootingTableRowData,
                             parameters: Map<string, ParameterMergedResult>,
                             actor: ActorObject,
                             actorsInPriorityOrder: ActorObject[]): TroubleshootingTableRowData[] {
    const rows: TroubleshootingTableRowData[] = [];
    const params = component.componentType === ComponentType.PERS_SETTINGS ?
        JSON.parse(component.content ?? "{}") : JSON.parse(component.content ?? "{}").defaultValues;
    const configOverrideParams = {
      ...params,
      ...JSON.parse(component.defaultConfiguration?.content ?? "{}"),
      ...JSON.parse(component.configuration?.content ?? "{}")
    };

    Object.keys(params).forEach((paramName: string) => {
      const priorityOverride = this.lookForMostPrioritizedOverride(
        paramName,
        parameters,
        actor,
        actorsInPriorityOrder
      );
      if (priorityOverride) {
        rows.push({
          ...commonRowData,
          comment: `Overridden by ${priorityOverride.sources.map(s => s.appId).join(', ')}`,
          status: StatusOptions.override,
          parameterName: paramName,
          parameterValue: configOverrideParams[paramName]
        });
        return;
      }
      // if the parameter is not overridden, we check if it is in conflict with another parameter
      const conflictsCandidates = Array.from(parameters.get(actor.actorName)?.conflicts.get(paramName) ?? []);
      if (conflictsCandidates.find(data => data.componentId === component.id)) {
        const conflictiveParameterMergedData = conflictsCandidates.filter(data => data.componentId !== component.id);
        rows.push({
          ...commonRowData,
          comment: `In conflict with ${conflictiveParameterMergedData.map(data => data.appId).join(', ')}`,
          status: StatusOptions.conflict,
          parameterName: paramName,
          parameterValue: configOverrideParams[paramName]
        });
      } else {
        rows.push({
          ...commonRowData,
          parameterName: paramName,
          parameterValue: configOverrideParams[paramName]
        });
      }
    });
    return rows;
  }

  /**
   * Look for the most prioritized override for a given parameter
   * @param paramName Name of the parameter to look for an override
   * @param parametersPerActor Map of parameters per actor
   * @param actor The actor object
   * @param actorsInPriorityOrder List of actors in priority order
   * @returns The most prioritized override or undefined
   */
  private lookForMostPrioritizedOverride(
    paramName: string,
    parametersPerActor: Map<string, ParameterMergedResult>,
    actor: ActorObject,
    actorsInPriorityOrder: ActorObject[]
  ): ParameterMergedValueSources | undefined {
    const startingIndex = actorsInPriorityOrder.findIndex(a => a.actorName === actor.actorName);
    if (startingIndex === -1 || startingIndex === actorsInPriorityOrder.length - 1) {
        // If the actor is not found or is the last one in the priority order, no override can be found
      return undefined;
    }
    let bestOverride: ParameterMergedValueSources | undefined = undefined;
    for (let i = startingIndex + 1; i < actorsInPriorityOrder.length; i++) {
      const nextActor = actorsInPriorityOrder[i];
      const nextParameters = parametersPerActor.get(nextActor.actorName);
      if (nextParameters && nextParameters.overrides.has(paramName)) {
        bestOverride = nextParameters.overrides.get(paramName);
      }
    }
    return bestOverride;
  }

  /**
   * Handle the script row in the table
   * @param component component object
   * @param commonRowData row data common in component families
   * @private
   */
  private handleScriptRow(component: ComponentObject,
                          commonRowData: TroubleshootingTableRowData): TroubleshootingTableRowData[] {
    let content = {
      urls: {internetUrl: BAD_FORMAT},
      autoLaunch: BAD_FORMAT,
      modalityType: BAD_FORMAT,
      getAsHttpRequest: BAD_FORMAT
    };
    try {
      content = JSON.parse(component.content ?? "{}");
    } catch (e) {
      return [{
        ...commonRowData,
        url: content.urls?.internetUrl,
        launchType: content.autoLaunch,
        modality: content.modalityType,
        httpsMethod: content.getAsHttpRequest,
        componentContent: component.content
      }];
    }

    return [{
      ...commonRowData,
      url: content.urls?.internetUrl,
      launchType: content.autoLaunch ? "Auto" : "Manual",
      modality: content.modalityType ?? '-',
      httpsMethod: content.getAsHttpRequest ? "GET" : "POST",
      componentContent: JSON.stringify(content, null, 2)
    }];
  }
}
